import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { bucketForRoute, hasWork, loadOverrides, matchKeyFor } from './mediaOverrides.js'

/**
 * CmsMediaLayer
 * -------------
 * Applies the Static Page (CMS) media decisions to whatever the page rendered:
 *
 *   replace -> swap the src / poster / background-image of a matching asset
 *   hide    -> take a source-declared asset off the live page
 *   add     -> clone the surrounding item and drop new media into the block
 *
 * It runs as a decoration pass over the DOM rather than threading a CMS prop
 * through ~35 page files, so every page is manageable without touching its
 * markup, and a CMS outage simply leaves the original assets in place.
 */

const MEDIA_ATTRS = {
  src: { origin: 'cmsSrcOrigin', applied: 'cmsSrcApplied' },
  poster: { origin: 'cmsPosterOrigin', applied: 'cmsPosterApplied' },
}

const BG_URL = /url\((['"]?)([^'")]+)\1\)/i

/* ------------------------------------------------------------------ replace */

function currentOrigin(el, attr) {
  const keys = MEDIA_ATTRS[attr]
  const live = el.getAttribute(attr) || ''
  const stored = el.dataset[keys.origin]
  const applied = el.dataset[keys.applied]

  // First sighting, or the app itself changed the asset (carousels, hover
  // swaps) — re-baseline so we never override against a stale original.
  if (stored === undefined || (live !== stored && live !== applied)) {
    el.dataset[keys.origin] = live
    delete el.dataset[keys.applied]
    return live
  }
  return stored
}

function applyAttr(el, attr, bucket) {
  const origin = currentOrigin(el, attr)
  if (!origin || origin.startsWith('data:') || origin.startsWith('blob:')) return

  const key = matchKeyFor(origin)
  if (!key) return
  const keys = MEDIA_ATTRS[attr]

  if (bucket.hide.includes(key) && attr === 'src') {
    if (el.dataset.cmsHidden !== '1') {
      el.dataset.cmsHidden = '1'
      el.dataset.cmsPrevDisplay = el.style.display || ''
      el.style.display = 'none'
    }
    return
  }
  if (el.dataset.cmsHidden === '1') {
    el.style.display = el.dataset.cmsPrevDisplay || ''
    delete el.dataset.cmsHidden
    delete el.dataset.cmsPrevDisplay
  }

  const next = bucket.replace[key]
  if (!next) return
  if (el.getAttribute(attr) === next) return

  el.dataset[keys.applied] = next
  el.setAttribute(attr, next)
  // A srcset would win over the src we just set.
  if (attr === 'src' && el.hasAttribute('srcset')) el.removeAttribute('srcset')
}

function applyBackground(el, bucket) {
  const live = el.style.backgroundImage || ''
  const stored = el.dataset.cmsBgOrigin
  const applied = el.dataset.cmsBgApplied
  let origin = stored
  if (stored === undefined || (live !== stored && live !== applied)) {
    origin = live
    el.dataset.cmsBgOrigin = live
    delete el.dataset.cmsBgApplied
  }
  const found = BG_URL.exec(origin || '')
  if (!found) return

  const key = matchKeyFor(found[2])
  const next = bucket.replace[key]
  if (!next) return
  const value = origin.replace(BG_URL, `url("${next}")`)
  if (el.style.backgroundImage === value) return
  el.dataset.cmsBgApplied = value
  el.style.backgroundImage = value
}

/* ---------------------------------------------------------------- additions */

/**
 * Walk up from an asset to the repeated unit it belongs to (the card / tile in
 * a grid), so a cloned copy lands as a sibling item instead of stacking inside
 * the original's own wrapper.
 */
function repeatUnit(el) {
  let node = el
  for (let i = 0; i < 6 && node.parentElement; i++) {
    if (node.parentElement.children.length >= 2) return node
    node = node.parentElement
  }
  return el
}

function buildAdded(anchor, item) {
  const unit = repeatUnit(anchor)
  let clone = unit.cloneNode(true)

  // Strip our bookkeeping from the copy so it gets re-evaluated cleanly.
  for (const node of [clone, ...clone.querySelectorAll('*')]) {
    if (!node.dataset) continue
    for (const k of Object.keys(node.dataset)) {
      if (k.startsWith('cms')) delete node.dataset[k]
    }
    node.style.removeProperty('display')
  }

  const wantTag = item.type === 'video' ? 'video' : 'img'
  let media = clone.matches?.('img, video') ? clone : clone.querySelector('img, video')

  if (media && media.tagName.toLowerCase() !== wantTag) {
    const swap = document.createElement(wantTag)
    swap.className = media.className
    swap.setAttribute('style', media.getAttribute('style') || '')
    // The clone is still detached, so replaceWith() would be a no-op when the
    // media element *is* the clone root — swap the root reference instead.
    if (media === clone) clone = swap
    else media.replaceWith(swap)
    media = swap
  }
  if (!media) {
    media = document.createElement(wantTag)
    media.className = 'h-full w-full object-cover'
    clone.appendChild(media)
  }

  media.removeAttribute('srcset')
  media.setAttribute('src', item.url)
  if (item.type === 'video') {
    media.muted = true
    media.loop = true
    media.playsInline = true
    media.setAttribute('controls', '')
  } else {
    media.setAttribute('alt', item.alt || item.name || '')
    media.setAttribute('loading', 'lazy')
  }

  clone.dataset.cmsAdded = item.url
  clone.setAttribute('title', item.name || '')
  return { unit, clone }
}

function applyAdditions(bucket) {
  for (const item of bucket.add) {
    if (!item || !item.url) continue
    if (document.querySelector(`[data-cms-added="${CSS.escape(item.url)}"]`)) continue

    let anchor = null
    if (item.anchor) {
      for (const el of document.querySelectorAll('img[src], video[src]')) {
        if (el.dataset.cmsAdded) continue
        const origin = el.dataset.cmsSrcOrigin ?? el.getAttribute('src') ?? ''
        if (matchKeyFor(origin) === item.anchor) { anchor = el; break }
      }
    }
    if (!anchor) continue

    const { unit, clone } = buildAdded(anchor, item)
    unit.parentElement?.insertBefore(clone, unit.nextSibling)
  }
}

/* ---------------------------------------------------------------- run / undo */

function resetDom() {
  for (const el of document.querySelectorAll('[data-cms-added]')) el.remove()

  for (const el of document.querySelectorAll('[data-cms-src-origin], [data-cms-poster-origin], [data-cms-bg-origin]')) {
    for (const [attr, keys] of Object.entries(MEDIA_ATTRS)) {
      const origin = el.dataset[keys.origin]
      if (origin === undefined) continue
      if (el.dataset[keys.applied] !== undefined) {
        if (origin) el.setAttribute(attr, origin)
        else el.removeAttribute(attr)
      }
      delete el.dataset[keys.origin]
      delete el.dataset[keys.applied]
    }
    if (el.dataset.cmsBgOrigin !== undefined) {
      if (el.dataset.cmsBgApplied !== undefined) el.style.backgroundImage = el.dataset.cmsBgOrigin
      delete el.dataset.cmsBgOrigin
      delete el.dataset.cmsBgApplied
    }
    if (el.dataset.cmsHidden === '1') {
      el.style.display = el.dataset.cmsPrevDisplay || ''
      delete el.dataset.cmsHidden
      delete el.dataset.cmsPrevDisplay
    }
  }
}

function applyAll(bucket) {
  for (const el of document.querySelectorAll('img[src], source[src]')) applyAttr(el, 'src', bucket)
  for (const el of document.querySelectorAll('video')) {
    if (el.hasAttribute('src')) applyAttr(el, 'src', bucket)
    if (el.hasAttribute('poster')) applyAttr(el, 'poster', bucket)
  }
  for (const el of document.querySelectorAll('[style*="background-image"]')) applyBackground(el, bucket)
  applyAdditions(bucket)
}

export default function CmsMediaLayer() {
  const { pathname } = useLocation()

  useEffect(() => {
    let cancelled = false
    let observer = null
    let frame = 0

    // The portal is the editor, not a managed page — leave its DOM alone.
    if (pathname.startsWith('/portal')) return undefined

    loadOverrides().then((overrides) => {
      if (cancelled) return
      const bucket = bucketForRoute(overrides, pathname)
      if (!hasWork(bucket)) return

      const run = () => {
        frame = 0
        if (!cancelled) applyAll(bucket)
      }
      // setTimeout rather than requestAnimationFrame: rAF is throttled to a
      // standstill in hidden/background tabs, which would leave a prerendered
      // page showing the pre-CMS assets.
      const schedule = () => {
        if (!frame) frame = setTimeout(run, 16)
      }

      run()
      observer = new MutationObserver(schedule)
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'srcset', 'poster', 'style'],
      })
    })

    return () => {
      cancelled = true
      if (frame) clearTimeout(frame)
      observer?.disconnect()
      resetDom()
    }
  }, [pathname])

  return null
}
